import { useCallback } from 'react';
import makeStyles from '@material-ui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import GridItem from 'components/Grid/GridItem';
import GridContainer from 'components/Grid/GridContainer';
import CardWrapper from 'components/Card/CardWrapper';
import CardBody from 'components/Card/CardBody';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';
import { Switch } from 'components/Form/Inputs';

import PlatformTile from './PlatformTile';
import {
  PLATFORM_OPTIONS,
  buildEmptyPlatformsState,
  type Platform,
  type PlatformConfig,
  type PlatformsState,
} from './platforms';

export { buildEmptyPlatformsState };

const useStyles = makeStyles({
  headerRow: {
    alignItems: 'center',
    '& > span': {
      marginTop: 0,
    },
  },
  allPlatformsSwitch: {
    marginLeft: 'auto',
    marginRight: '15px',
  },
});

type RestrictionsSectionState = {
  platforms: PlatformsState;
  restrictPlatforms: boolean;
  [key: string]: unknown;
};

type PlatformsSectionProps = {
  platforms: PlatformsState;
  restrictPlatforms: boolean;
  setRestrictionsSection: (
    updater: (prev: RestrictionsSectionState) => RestrictionsSectionState
  ) => void;
  isPercent: boolean;
  isEdit: boolean;
};

const PlatformsSection = ({
  platforms,
  restrictPlatforms,
  setRestrictionsSection,
  isPercent,
  isEdit,
}: PlatformsSectionProps) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const updatePlatform = useCallback(
    (platform: Platform, changes: Partial<PlatformConfig>) => {
      setRestrictionsSection(prev => ({
        ...prev,
        platforms: {
          ...prev.platforms,
          [platform]: { ...prev.platforms[platform], ...changes },
        },
      }));
    },
    [setRestrictionsSection]
  );

  const handleRestrictToggle = (restrict: boolean) => {
    setRestrictionsSection(prev => ({
      ...prev,
      restrictPlatforms: restrict,
      platforms: restrict ? prev.platforms : buildEmptyPlatformsState(),
    }));
  };

  return (
    <CardWrapper
      title={t('discountCodes.platforms.title')}
      classesOverride={{ parentTitle: classes.headerRow }}
      tooltip={
        <InfoIconTooltip
          title={
            <div>
              <p>{t('discountCodes.platforms.tooltip1')}</p>
              <p>{t('discountCodes.platforms.tooltip2')}</p>
              {t('discountCodes.platforms.tooltip3')}
            </div>
          }
        />
      }
      additionalContentAfterTitle={
        <Switch
          className={classes.allPlatformsSwitch}
          value={restrictPlatforms}
          onChange={handleRestrictToggle}
          label={t('discountCodes.platforms.restrictToSelected')}
          disabled={isEdit}
        />
      }
    >
      <CardBody>
        {restrictPlatforms && (
          <GridContainer>
            {PLATFORM_OPTIONS.map(platform => (
              <GridItem sm={12} md={4} key={platform}>
                <PlatformTile
                  platform={platform}
                  config={platforms[platform]}
                  isPercent={isPercent}
                  isEdit={isEdit}
                  onChange={updatePlatform}
                />
              </GridItem>
            ))}
          </GridContainer>
        )}
      </CardBody>
    </CardWrapper>
  );
};

export default PlatformsSection;
